import { createAction, createSlice, current } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import { apiCallBegan } from "./api";
import {
  DEFAULT_SELECTED_OPTION,
  EMPTY_COVER_ROW_DETAILS,
  getCalculateVerticalIdFromProspectDoc,
  getCoverIdForCoverageLevel,
  getCoverIdOfPrice,
  getDefaultSelectedOption,
} from "./businesslogic/hardwiredlogic";
import { addTransaction } from "../store/transactions";
import {
  convertArrayOfObjectsToId,
  insurerarrayToObject,
  filterOnlyManualInsurer,
  filterOnlyManualInsurerInverseVertical,
  PossibleIncludanceValues,
  removeElementFromArray,
  PossibleImpCoverValues,
  PossibleExcelApiStatusValues,
  arrayToObject,
  PossibleApiStatusValues,
} from "./helperfuncs";
import _ from "lodash";

// bp stands for broker against prospect

//#region HELPER FUNCTIONS

const convertResponseDetailsToOrderAsPerRank = (responsedetails) => {
  let sortable = [];
  for (let responseid in responsedetails) {
    sortable.push([responseid, responsedetails[responseid]["rank"]]);
  }

  sortable.sort(function (a, b) {
    return b[1] - a[1];
  });

  let responseorder = [];
  sortable.forEach((array) => {
    responseorder.push(array[0]);
  });

  return responseorder;
};

const initializeSomeProperties = ({ responsedetails }) => {
  let returnedValue = responsedetails;
  try {
    let responsedetails_copy = { ...responsedetails };

    for (let responseid in responsedetails_copy) {
      responsedetails_copy[responseid].excelApiStatus =
        PossibleExcelApiStatusValues.notstarted;
      responsedetails_copy[responseid].isViewQuoteModalOpen = false;
      if (responsedetails_copy[responseid]?.otherinfo?.bindType) {
        responsedetails_copy[responseid].bindtype =
          responsedetails_copy[responseid].otherinfo.bindType;
      }
    }

    returnedValue = responsedetails_copy;
  } catch (e) {
    returnedValue = responsedetails;
  }

  return returnedValue;
};

const convertCoversArrayToObject = ({ coversArray }) => {
  let coversObject = {};

  coversArray.forEach((covervalue, index) => {
    coversObject[index + 1] = covervalue;
  });

  return coversObject;
};

const decideAndSeperateIfDesiredValueObtainedFromExcel = ({
  response,
  covers,
}) => {
  let returnedValue = {
    excelApiStatus: PossibleExcelApiStatusValues.failed,
    covers: covers,
  };
  if (response.error) {
    return returnedValue;
  }

  if (!_.isEmpty(response?.data?.covers)) {
    returnedValue.covers = convertCoversArrayToObject({
      coversArray: response.data.covers,
    });
    returnedValue.excelApiStatus = PossibleExcelApiStatusValues.success;
  }

  return returnedValue;
};

const totalResponseSelectedForCompare = ({
  responsedetails,
  responseorder,
}) => {
  let total = 0;
  if (responseorder) {
    responseorder.forEach((responseid) => {
      if (responsedetails[responseid].checkedforcompare === true) total++;
    });
  }
  return total;
};

const getSelectedResponseIds = ({ responsedetails, responseorder }) => {
  let responseIds = [];
  if (responseorder) {
    responseorder.forEach((responseid) => {
      if (responsedetails[responseid].checkedforcompare === true)
        responseIds.push(responseid);
    });
  }
  return responseIds;
};

const detailsOfResponseSelectedForCompare = ({
  responsedetails,
  responseorder,
  insurerdetails,
  languagecode = "en",
}) => {
  let returnValue = [getDefaultSelectedOption(languagecode)];
  if (responseorder) {
    responseorder.forEach((responseid) => {
      let thisresponsedetails = responsedetails[responseid];
      if (thisresponsedetails.checkedforcompare === true) {
        returnValue.push({
          id: thisresponsedetails.insurercode,
          text: insurerdetails[thisresponsedetails.insurercode].name,
        });
      }
    });
  }
  return returnValue;
};

const addRowInTable = ({ table }) => {
  let oldTable = { ...table };

  let newTable = {};

  //#region table order
  // find max of table order

  let max = -1;

  try {
    let tempArray = oldTable.order ? oldTable.order : [];
    max = Math.max(...tempArray);
    console.log("CCCCCCCCCCCCCCA SADF", max);

    if (isNaN(max)) max = 0;
    if (max === null || max === Infinity || max === -Infinity) max = 0;
  } catch (e) {
    console.log("ERROR: CANNOT FIND MAX IN COVERTABLE ORDER");
    max = 0;
  }
  newTable.order = [...oldTable.order, max + 1];
  newTable.details = {
    ...oldTable.details,
    [max + 1]: EMPTY_COVER_ROW_DETAILS,
  };
  //#endregion

  return newTable;
};

const deleteRowInTable = ({ table, rowid }) => {
  let oldTable = { ...table };
  let newTable = {};

  if (oldTable.order.length === 1)
    return {
      order: [0],
      details: { 0: EMPTY_COVER_ROW_DETAILS },
    };

  newTable.order = _.remove(oldTable.order, (n) => n != rowid);

  delete oldTable.details[rowid];

  newTable.details = oldTable.details;

  return newTable;
};

const reasonUpdate = ({ table, rowid, value }) => {
  let oldTable = { ...table };

  oldTable.details[rowid].reason = value;

  return oldTable;
};

const coverUpdate = ({ table, rowid, value }) => {
  let oldTable = { ...table };

  oldTable.details[rowid].cover = value;

  return oldTable;
};

const calculateQuoteyScore = ({ responseorder, responsedetails }) => {
  // step 1 Ready the only responsedetails value that have the covermatch and price in number

  let arrayOfObjects = []; // this will be in form of arrys of objects of {responseid: "bhndwiskds50sd5g0sd", price: 250, covermatch: 57}
  let objectsOfResponses = {}; // this will be in form of objects {bhndwiskds50sd5g0sd:{price: 250, coverage: 57} }
  responseorder.forEach((responseid) => {
    if (
      responsedetails[responseid]?.covers &&
      !isNaN(responsedetails[responseid]?.covers[getCoverIdOfPrice()]) &&
      !isNaN(responsedetails[responseid]?.covers[getCoverIdForCoverageLevel()])
    ) {
      if (
        !(
          responsedetails[responseid]?.covers[getCoverIdOfPrice()] === "" ||
          responsedetails[responseid]?.covers[getCoverIdForCoverageLevel()] ===
            ""
        )
      ) {
        let ob = {
          responseid,
          price: Number(
            responsedetails[responseid].covers[getCoverIdOfPrice()]
          ),
          coverage: Number(
            responsedetails[responseid].covers[getCoverIdForCoverageLevel()]
          ),
        };
        arrayOfObjects.push({ ...ob });
        objectsOfResponses[responseid] = { ...ob };
      }
    }
  });

  //#region caculate quotey score

  let returnValue = {};

  let minpriceobject = null;
  let maxcoverageobject = null;

  try {
    minpriceobject = [...arrayOfObjects].reduce(function (prev, current) {
      return prev.price < current.price ? prev : current;
    });

    maxcoverageobject = [...arrayOfObjects].reduce(function (prev, current) {
      return prev.coverage > current.coverage ? prev : current;
    });
  } catch (err) {
    returnValue = false;
  }

  let RSCORE = {};
  if (returnValue) {
    const minprice = minpriceobject.price;
    const maxcoverage = maxcoverageobject.coverage;

    for (let responseid in objectsOfResponses) {
      try {
        RSCORE[responseid] = Math.round(
          20 *
            ((2 * minprice) / objectsOfResponses[responseid].price +
              (3 * objectsOfResponses[responseid].coverage) / maxcoverage)
        );
      } catch (e) {
        RSCORE[responseid] = 100;
      }
    }
  }

  //#endregion

  return { ...RSCORE };
};

const udpateQuoteyScore = ({ responsedetails, quoteyScores }) => {
  for (let responseid in quoteyScores) {
    responsedetails[responseid].quoteyscore = quoteyScores[responseid];
  }
};

const getQuoteyScoresForAllResponses = ({ responseorder, responsedetails }) => {
  let returnObject = {};

  responseorder.forEach((responseid) => {
    returnObject[responseid] = responsedetails[responseid]?.quoteyscore;
  });

  return { ...returnObject };
};

const compareOldVsNewQuoteyScores = ({
  oldQuoteyScores,
  newQuoteyScores,
  responseorder,
}) => {
  let returnArray = [];

  responseorder.forEach((responseid) => {
    if (oldQuoteyScores[responseid] != newQuoteyScores[responseid]) {
      returnArray.push({
        responseid,
        value: newQuoteyScores[responseid],
      });
    }
  });

  return returnArray;
};
//#endregion

const slice = createSlice({
  name: "quotes",
  initialState: {
    isbpVerified: false,
    verifyingbp: false,
    prospectid: null,
    responseorder: [],
    responsedetails: {},
    prospectdetails: {},
    insurerdetails: {},
    insurers: [],
    insurerorder: [],
    isScrollDivResponses: false,
    showingSendToInsurers: false,
    irs: null,
    crs: null,
    comment: null,
    stiEmailData: null,
    recalculate: null,
    totalselected: 0,
    isSTCModalopen: false,
    stcoptions: null || [],
    insurerCheckBox: [],
    insureorderremaining: [],
    brokerteam: PossibleApiStatusValues.notstarted,
    aboutus: PossibleApiStatusValues.notstarted,
    coversAndCoverTypes: PossibleApiStatusValues.notstarted,
    totalApiCallsToGetResponseInProgress: 0,
    totalApiCallsCompleted: 0,
    totalApiCallsCalled: 0,
    totalApiCallsToGetResponseInProgressPreviousValue: null,
    locationAddresses: {},
    policyJustBinded: false,
  },
  reducers: {
    // action => action handler

    bpVerificationInitialized: (quotes, action) => {
      quotes.verifyingbp = true;
    },
    bpVerified: (quotes, action) => {
      quotes.verifyingbp = false;
      quotes.isbpVerified = true;
      quotes.irs = action.payload.irs;
      quotes.crs = action.payload.crs;
      quotes.covertable = arrayToObject(action.payload.covers, "id");
      quotes.coverimportances = action.payload.coverimportances;
      quotes.recalculate = action.payload.recalculate;
      quotes.covertypes = action.payload.covertypes;
      quotes.responseorder = convertResponseDetailsToOrderAsPerRank(
        action.payload.responsedetails
      );
      quotes.responsedetails = initializeSomeProperties({
        responsedetails: action.payload.responsedetails,
      });
      quotes.prospectdetails = action.payload.prospectdetails;
      quotes.stcoptions = quotes.prospectdetails.stcoptions;
      quotes.insurers = action.payload.insurers;
      quotes.insurerdetails = insurerarrayToObject(
        action.payload.insurers,
        "code"
      );
      quotes.insurerorder = convertArrayOfObjectsToId(
        filterOnlyManualInsurer(
          action.payload.insurers,
          getCalculateVerticalIdFromProspectDoc(action.payload.prospectdetails)
        ),
        "code"
      );
      // quotes.insureorderremaining = convertArrayOfObjectsToId(
      //   filterOnlyManualInsurerInverseVertical(
      //     action.payload.insurers,
      //     getCalculateVerticalIdFromProspectDoc(action.payload.prospectdetails)
      //   ),
      //   "code"
      // );
      quotes.totalselected = totalResponseSelectedForCompare({
        responsedetails: quotes.responsedetails,
        responseorder: quotes.responseorder,
      });
      quotes.locationAddresses = action.payload.locationAddresses;
    },
    bpVerificationFailed: (quotes, action) => {
      quotes.verifyingbp = false;
      quotes.isbpVerified = false;
    },

    initiateInsurerOrderRemaining: (quotes, action) => {
      quotes.insureorderremaining = convertArrayOfObjectsToId(
        filterOnlyManualInsurerInverseVertical(
          quotes.insurers,
          getCalculateVerticalIdFromProspectDoc(quotes.prospectdetails)
        ),
        "code"
      );
    },

    updateInsurerOrder: (quotes, action) => {
      quotes.insurerorder = action.payload;
    },
    updateInsurerOrderInverse: (quotes, action) => {
      quotes.insureorderremaining = action.payload;
    },

    udpateQuoteyScoreInStore: (quotes, action) => {
      let quoteyScores = calculateQuoteyScore({
        responsedetails: quotes.responsedetails,
        responseorder: quotes.responseorder,
      });
      udpateQuoteyScore({
        responsedetails: quotes.responsedetails,
        quoteyScores,
      });
    },

    storeProspectid: (quotes, action) => {
      quotes.prospectid = action.payload;
    },

    changeQuoteyScoreValue: (quotes, action) => {
      quotes.responsedetails[action.payload.responseid].quoteyscore =
        action.payload.value;
    },

    excelApiCalled: (quotes, action) => {
      quotes.responsedetails[action.payload.responseid].errorMsg = null;
      quotes.responsedetails[action.payload.responseid].excelApiStatus =
        PossibleExcelApiStatusValues.loading;
    },
    updateBindTypeCalled: (quotes, action) => {
      if (action.payload.extras) {
        quotes.responsedetails[action.payload.extras.responseid].bindtype =
          action.payload.extras.bindType;
        if (action.payload.extras.underwriterInfo) {
          let otherinfo =
            quotes.responsedetails[action.payload.extras.responseid].otherinfo;
          quotes.responsedetails[action.payload.extras.responseid].otherinfo = {
            ...otherinfo,
            underwriterInfo: action.payload.extras.underwriterInfo,
          };
        }
      }
      if (action.payload && action.payload.responseid) {
        // to change 1,3 bind to 7 ( pending underwriter review )
        quotes.responsedetails[action.payload.responseid].bindtype =
          action.payload.bindtype;
      }
    },
    getPolicyCalled: (quotes, action) => {
      if (action.payload.extras) {
        quotes.responsedetails[action.payload.extras?.responseid].policy =
          action.payload.response;
      }
    },
    excelApiFailed: (quotes, action) => {
      quotes.responsedetails[action.payload.extras.responseid].excelApiStatus =
        PossibleExcelApiStatusValues.failed;
    },

    excelApiSuccess: (quotes, action) => {
      let returnedObject = decideAndSeperateIfDesiredValueObtainedFromExcel({
        covers: quotes.responsedetails[action.payload.extras.responseid].covers,
        response: action.payload.response,
      });

      quotes.responsedetails[action.payload.extras.responseid].excelApiStatus =
        returnedObject.excelApiStatus;
      quotes.responsedetails[action.payload.extras.responseid].covers =
        returnedObject.covers;
      if (action.payload.response?.data?.files) {
        quotes.responsedetails[action.payload.extras.responseid].files =
          action.payload.response.data.files;
      }
      if (action.payload.response?.data?.bindType) {
        quotes.responsedetails[action.payload.extras.responseid].bindtype =
          action.payload.response?.data?.bindType;
      }
      if (action.payload.response.error && action.payload.response.message) {
        quotes.responsedetails[action.payload.extras.responseid].errorMsg =
          action.payload.response.message;
      }
    },

    changeIsViewQuoteModalOpen: (quotes, action) => {
      quotes.responsedetails[action.payload.responseid].isViewQuoteModalOpen =
        action.payload.value;
    },

    changeScrollDivResponse: (quotes, action) => {
      quotes.isScrollDivResponses = action.payload;
    },

    showingSendToInsurers: (quotes, action) => {
      quotes.showingSendToInsurers = action.payload;
    },

    changeSelectedEmail: (quotes, action) => {
      quotes.insurerdetails[action.payload.code]["selectedemail"] =
        action.payload.selectedemail;
    },

    getIncludanceInitialized: (quotes, action) => {
      quotes.insurerdetails[action.payload.code]["includance"] =
        PossibleIncludanceValues.awaiting;
    },

    getIncludanceSuccess: (quotes, action) => {
      quotes.insurerdetails[action.payload.extras.code]["includance"] = action
        .payload.response.exist
        ? PossibleIncludanceValues.includedinpast
        : PossibleIncludanceValues.neverincluded;
    },

    getIncludanceFailed: (quotes, action) => {
      quotes.insurerdetails[action.payload.extras.code]["includance"] =
        PossibleIncludanceValues.failed;
    },

    getIncludanceAllInitialized: (quotes, action) => {
      for (var insurer in quotes.insurerdetails) {
        quotes.insurerdetails[insurer]["includance"] =
          PossibleIncludanceValues.awaiting;
      }
      // quotes.insurerdetails[action.payload.code]["includance"] =
      //   PossibleIncludanceValues.awaiting;
    },

    getIncludanceAllSuccess: (quotes, action) => {
      var existingInsurers = action.payload.response.responseid.map(
        (x) => x.insurercode
      );
      for (var insurer in quotes.insurerdetails) {
        if (existingInsurers.includes(insurer)) {
          quotes.insurerdetails[insurer]["includance"] =
            PossibleIncludanceValues.includedinpast;
        } else {
          quotes.insurerdetails[insurer]["includance"] =
            PossibleIncludanceValues.neverincluded;
        }
      }
    },

    getIncludanceAllFailed: (quotes, action) => {
      quotes.insurerdetails[action.payload.extras.code]["includance"] =
        PossibleIncludanceValues.failed;
    },

    changeSelected: (quotes, action) => {
      quotes.insurerdetails[action.payload.code]["selected"] =
        !quotes.insurerdetails[action.payload.code]["selected"];
    },

    singleResponseAddInitialized: (quotes, action) => {
      quotes.insurerdetails[action.payload.code]["includance"] =
        PossibleIncludanceValues.awaiting;
    },

    singleResponseAddSuccess: (quotes, action) => {
      quotes.insurerdetails[action.payload.extras.code]["includance"] = action
        .payload.response.error
        ? PossibleIncludanceValues.failed
        : PossibleIncludanceValues.incudedthistime;

      quotes.insurerdetails[action.payload.extras.code]["selected"] = true;
    },

    singleResponseAddFailed: (quotes, action) => {
      quotes.insurerdetails[action.payload.extras.code]["includance"] =
        PossibleIncludanceValues.failed;
      quotes.insurerdetails[action.payload.extras.code]["selected"] = false;
    },

    singleResponseRemoveInitialized: (quotes, action) => {
      quotes.insurerdetails[action.payload.code]["includance"] =
        PossibleIncludanceValues.awaiting;
    },

    singleResponseRemoveSuccess: (quotes, action) => {
      quotes.insurerdetails[action.payload.extras.code]["includance"] = action
        .payload.response.error
        ? PossibleIncludanceValues.failed
        : PossibleIncludanceValues.neverincluded;

      quotes.insurerdetails[action.payload.extras.code]["selected"] = false;
    },

    singleResponseRemoveFailed: (quotes, action) => {
      quotes.insurerdetails[action.payload.extras.code]["includance"] =
        PossibleIncludanceValues.failed;
      quotes.insurerdetails[action.payload.extras.code]["selected"] = true;
    },

    changeCovers: (quotes, action) => {
      quotes.responsedetails[action.payload.responseid].covers =
        action.payload.covers;
    },

    changeResponseStarrating: (quotes, action) => {
      quotes.responsedetails[action.payload.responseid].starrating =
        action.payload.starrating;
    },

    starRatingUpdateFailed: (quotes, action) => {
      quotes.responsedetails[action.payload.extras.responseid].starrating =
        action.payload.extras.previousvalue;
    },

    deleteResponse: (quotes, action) => {
      removeElementFromArray(quotes.responseorder, action.payload.responseid);
    },

    deleteResponseFailed: (quotes, action) => {
      quotes.responseorder = action.payload.extras.responsearraybeforedelete;
    },
    reassignRepsonseOrder: (quotes, action) => {
      quotes.responseorder = action.payload;
    },

    updateResponseFailed: (quotes, action) => {
      quotes.updateError = action.payload;
    },

    gettingDataRequiredForEmailInitialized: (quotes, action) => {
      quotes.stiEmailData = PossibleApiStatusValues.loading;
    },

    gotDataRequiredForEmailFailed: (quotes, action) => {
      quotes.stiEmailData = PossibleApiStatusValues.failed;
    },

    gotDataRequiredForEmailSuccess: (quotes, action) => {
      quotes.stiEmailData = action.payload;
    },
    // setResponseDetailsIsChecked: (quotes, action) => {
    //   quotes.responsedetails[action.payload.insurerid].checkedforcompare =
    //     action.payload.checked;

    getCurrentCoverLoading: (quotes, action) => {
      quotes.isLoading = true;
    },
    getCurrentCoversSuccess: (quotes, action) => {
      quotes.comment = action.payload.comment;
      quotes.covers = action.payload.covers;
      quotes.covertable = arrayToObject(action.payload.covers, "id");
      quotes.locationcovers = action.payload.locations;
      quotes.coverimportances = action.payload.coverimportances;
      quotes.isLoading = false;
    },
    getCurrentCoversPlansSuccess: (quotes, action) => {
      quotes.plans = action.payload;
      quotes.isLoading = false;
    },
    getCurrentCoversFailed: (quotes, action) => {
      quotes.isbpVerified = false;
    },

    checkedForCompareChanged: (quotes, action) => {
      const { responseid, checkedstate } = action.payload;
      quotes.responsedetails[responseid].checkedforcompare = checkedstate;
    },

    updateTotalSelected: (quotes, action) => {
      quotes.totalselected = totalResponseSelectedForCompare({
        responsedetails: quotes.responsedetails,
        responseorder: quotes.responseorder,
      });
    },

    updateSTCModalStatus: (quotes, action) => {
      quotes.isSTCModalopen = action.payload.status;
    },
    updateSTCOptions: (quotes, action) => {
      quotes.stcoptions = action.payload.stcoptions;
    },
    uploadInsurerCheckBoxValue: (quotes, action) => {
      quotes.insurerCheckBox = action.payload;
    },

    updateSTCoptionSelectedValue: (quotes, action) => {
      const { key, value } = action.payload;
      if (!quotes.stcoptions) {
        quotes.stcoptions = {};
      }
      if (!quotes.stcoptions[key]) {
        quotes.stcoptions[key] = {};
      }
      quotes.stcoptions[key].selected = value;
    },

    updateSTCoptionComparisonTableValue: (quotes, action) => {
      quotes.stcoptions[action.payload.key].tableValue = action.payload.value;
    },
    updateSTCoptionRawQuoteInformation: (quotes, action) => {
      quotes.stcoptions[action.payload.key].rawQuoteInformation =
        action.payload.value;
    },

    updateTeamLoadingStatus: (quotes, action) => {
      quotes.brokerteam = action.payload;
    },

    updateAboutUsStatus: (quotes, action) => {
      quotes.aboutus = action.payload;
    },

    updateStcOptionsTeamSelected: (quotes, action) => {
      quotes.stcoptions.meetyourteam.teamselected =
        action.payload.selectedteamsarray;
    },

    updateStcOptionsInsurerSelected: (quotes, action) => {
      quotes.stcoptions.comparison.insurerselected =
        action.payload.selectedoption;
    },

    updateStcOptionsAboutUsSelected: (quotes, action) => {
      quotes.stcoptions.aboutus.aboutusselected = action.payload.selectedoption;
    },

    updateStcOptionsReasonForRecommendation: (quotes, action) => {
      quotes.stcoptions.comparison.reasonforrecommendation =
        action.payload.value;
    },

    updateStcOptionsAddResponsesSelected: (quotes, action) => {
      quotes.stcoptions.responseids = getSelectedResponseIds({
        responsedetails: quotes.responsedetails,
        responseorder: quotes.responseorder,
      });
      // also store crs require for pdf
      quotes.stcoptions.crs = quotes.crs;
    },

    addTableRow: (quotes, action) => {
      quotes.stcoptions.covers.table = addRowInTable({
        table: quotes.stcoptions.covers.table,
      });
    },
    deleteTableRow: (quotes, action) => {
      quotes.stcoptions.covers.table = deleteRowInTable({
        table: quotes.stcoptions.covers.table,
        rowid: action.payload.rowid,
      });
    },
    updateReason: (quotes, action) => {
      quotes.stcoptions.covers.table = reasonUpdate({
        table: quotes.stcoptions.covers.table,
        rowid: action.payload.rowid,
        value: action.payload.value,
      });
    },

    updateCoverInRow: (quotes, action) => {
      quotes.stcoptions.covers.table = coverUpdate({
        table: quotes.stcoptions.covers.table,
        rowid: action.payload.rowid,
        value: action.payload.value,
      });
    },

    coversAndCoverTypeFetchBegan: (quotes, action) => {
      quotes.coversAndCoverTypes = PossibleApiStatusValues.loading;
    },
    coversAndCoverTypeFetchSuccess: (quotes, action) => {
      quotes.coversAndCoverTypes = action.payload.list;
    },

    addToTotalApiCallsToGetResponse: (quotes, action) => {
      quotes.totalApiCallsToGetResponseInProgressPreviousValue =
        quotes.totalApiCallsToGetResponseInProgress;
      quotes.totalApiCallsToGetResponseInProgress++;
      quotes.totalApiCallsCalled++;
    },
    subtractToTotalApiCallsToGetResponse: (quotes, action) => {
      if (quotes.totalApiCallsToGetResponseInProgress > 0) {
        quotes.totalApiCallsToGetResponseInProgressPreviousValue =
          quotes.totalApiCallsToGetResponseInProgress;
        quotes.totalApiCallsToGetResponseInProgress--;
        quotes.totalApiCallsCompleted++;
      }
    },

    udpatepolicyJustBinded: (quotes, action) => {
      quotes.policyJustBinded = action.payload.value;
    },
  },
});

export const {
  bpVerificationInitialized,
  bpVerified,
  bpVerificationFailed,
  storeProspectid,
  addResponse,
  changeScrollDivResponse,
  showingSendToInsurers,
  initiateInsurerOrderRemaining,
  changeSelectedEmail,
  changeIsViewQuoteModalOpen,
  getIncludanceInitialized,
  getIncludanceSuccess,
  getIncludanceFailed,
  getIncludanceAllInitialized,
  getIncludanceAllSuccess,
  getIncludanceAllFailed,
  changeSelected,
  singleResponseAddInitialized,
  singleResponseAddSuccess,
  singleResponseAddFailed,
  singleResponseRemoveInitialized,
  singleResponseRemoveSuccess,
  singleResponseRemoveFailed,
  changeQuoteyScoreValue,
  changeCovers,
  changeResponseStarrating,
  starRatingUpdateFailed,
  deleteResponse,
  deleteResponseFailed,
  updateResponseFailed,
  reassignRepsonseOrder,
  excelApiCalled,
  updateBindTypeCalled,
  getPolicyCalled,
  excelApiSuccess,
  excelApiFailed,
  gettingDataRequiredForEmailInitialized,
  gotDataRequiredForEmailFailed,
  gotDataRequiredForEmailSuccess,
  // setResponseDetailsIsChecked,
  checkedForCompareChanged,
  getCurrentCoverLoading,
  getCurrentCoversSuccess,
  getCurrentCoversFailed,
  updateTotalSelected,
  updateSTCModalStatus,
  updateSTCOptions,
  uploadInsurerCheckBoxValue,
  updateSTCoptionSelectedValue,
  updateSTCoptionComparisonTableValue,
  updateSTCoptionRawQuoteInformation,
  updateTeamLoadingStatus,
  updateAboutUsStatus,
  updateStcOptionsTeamSelected,
  updateStcOptionsInsurerSelected,
  updateStcOptionsAboutUsSelected,
  getCurrentCoversPlansSuccess,
  updateStcOptionsReasonForRecommendation,
  addTableRow,
  deleteTableRow,
  updateReason,
  updateCoverInRow,
  coversAndCoverTypeFetchSuccess,
  coversAndCoverTypeFetchBegan,
  addToTotalApiCallsToGetResponse,
  subtractToTotalApiCallsToGetResponse,
  udpateQuoteyScoreInStore,
  udpatepolicyJustBinded,
  updateStcOptionsAddResponsesSelected,
  updateInsurerOrder,
  updateInsurerOrderInverse,
} = slice.actions;

export default slice.reducer;

//#region Action Creators

// To get the data required by Email to sent from Send to Insurer
export const getDataRequiredForEmail = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/prospects/dataforemail",
    method: "post",
    data: data,
    onStart: gettingDataRequiredForEmailInitialized.type,
    onSuccess: gotDataRequiredForEmailSuccess.type,
    onError: gotDataRequiredForEmailFailed.type,
  });

export const getBrokerTeamInfo = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/brokers/getbrokersforbrokeragecode",
    method: "post",
    data: data,
    onSuccess: updateTeamLoadingStatus.type,
  });

export const getAboutUsListFromDb = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/brokerages/aboutus",
    method: "post",
    data: data,
    onSuccess: updateAboutUsStatus.type,
  });

export const verifyBrokerAgainstProspect = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/prospects/getdetailedprospect",
    method: "post",
    data: data,
    onStart: bpVerificationInitialized.type,
    onSuccess: bpVerified.type,
    onError: bpVerificationFailed.type,
  });

export const applyInsurerPlan = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/quote/plan",
    method: "put",
    data: data,
    onError: "d12",
    onSuccess: "deleted",
    considerPayloads: true,
    onErrorPayload: {
      responseid: data.responseid,
      previousvalue: data.previousvalue,
    },
  });
export const getCurrentCoversPlans = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/responses/getCurrentCoversPlans?responseid=" + data,
    method: "get",
    onStart: getCurrentCoverLoading.type,
    onSuccess: getCurrentCoversPlansSuccess.type,
    onError: false,
  });
export const getInhouseQuotes = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/iic/quote/inhouse",
    method: "post",
    data: data,
    onStart: excelApiCalled.type,
    onSuccess: excelApiSuccess.type,
    onError: excelApiFailed.type,
    considerPayloads: true,
    onStartPayload: { responseid: data.responseid },
    onSuccessPayload: { responseid: data.responseid },
    onErrorPayload: { responseid: data.responseid },
  });
export const getExternalQuotes = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/iic/quote/external",
    method: "post",
    data: data,
    onStart: excelApiCalled.type,
    onSuccess: excelApiSuccess.type,
    onError: excelApiFailed.type,
    considerPayloads: true,
    onStartPayload: { responseid: data.responseid },
    onSuccessPayload: { responseid: data.responseid },
    onErrorPayload: { responseid: data.responseid },
  });

export const postCoaltionUrls = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/eic/coalition/urls",
    method: "post",
    data: data,
  });

export const updateBindType = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/responses/updateBindType",
    method: "post",
    data: data,
    onStart: updateBindTypeCalled.type,
    onSuccess: updateBindTypeCalled.type,
    considerPayloads: true,
    onStartPayload: { ...data },
    onSuccessPayload: { ...data },
    onErrorPayload: { code: data.responseid },
  });

export const getTempPolicy = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/responses/getTempPolicy",
    method: "post",
    data: data,
    onStart: getPolicyCalled.type,
    onSuccess: getPolicyCalled.type,
    considerPayloads: true,
    onStartPayload: { ...data },
    onSuccessPayload: { ...data },
    onErrorPayload: { code: data.responseid },
  });

export const getCurrentCovers = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/responses/getCurrentCovers?responseid=" + data,
    method: "get",
    onStart: getCurrentCoverLoading.type,
    onSuccess: getCurrentCoversSuccess.type,
    onError: getCurrentCoversFailed.type,
  });

export const getIncludance = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/responses/checkrecordexistance",
    method: "post",
    data: data,
    onStart: getIncludanceInitialized.type,
    onSuccess: getIncludanceSuccess.type,
    onError: getIncludanceFailed.type,
    considerPayloads: true,
    onStartPayload: { code: data.insurercode },
    onSuccessPayload: { code: data.insurercode },
    onErrorPayload: { code: data.insurercode },
  });

export const getIncludanceAll = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/responses/checkrecordexistanceAll",
    method: "post",
    data: data,
    onStart: getIncludanceAllInitialized.type,
    onSuccess: getIncludanceAllSuccess.type,
    onError: getIncludanceAllFailed.type,
    considerPayloads: true,
    onStartPayload: { code: data.insurercode },
    onSuccessPayload: { code: data.insurercode },
    onErrorPayload: { code: data.insurercode },
  });

export const updateStarRating = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/responses/updaterating",
    method: "post",
    data: data,
    onError: starRatingUpdateFailed.type,
    considerPayloads: true,
    onErrorPayload: {
      responseid: data.responseid,
      previousvalue: data.previousvalue,
    },
  });

export const updateSTCOptionsToDB = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/prospects/updatestcoptions",
    method: "post",
    data: data,
  });

export const updateCover = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/responses/updateResponse",
    method: "post",
    data: data,
    onError: updateResponseFailed.type,
  });

export const updatePolicyCovers = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/responses/updatePolicyCovers",
    method: "post",
    data: data,
    onError: updateResponseFailed.type,
  });

export const getCoversAndCoverTypes = (data) =>
  apiCallBegan({
    serviceUrl: process.env.REACT_APP_BASICSERVICE,
    url: "/api/covers/coversandcovertypes",
    method: "get",
    data: data,
    onStart: coversAndCoverTypeFetchBegan.type,
    onSuccess: coversAndCoverTypeFetchSuccess.type,
  });

export const deleteResponseFromDb =
  ({ responseid, insurercode }) =>
  (dispatch, getState) => {
    const prospectid = getState().entities.quotes.prospectid;
    const responsearraybeforedelete = getState().entities.quotes.responseorder;

    dispatch(deleteResponse({ responseid }));

    dispatch(
      apiCallBegan({
        serviceUrl: process.env.REACT_APP_BASICSERVICE,
        url: "/api/responses/removesingleresponse",
        method: "post",
        data: { prospectid, insurercode },
        onError: deleteResponseFailed.type,
        considerPayloads: true,
        onErrorPayload: {
          responsearraybeforedelete,
        },
      })
    );

    return;
  };

export const updateCheckedForCompareToDb =
  ({ responseid, checkedstate }) =>
  (dispatch, getState) => {
    const prospectid = getState().entities.quotes.prospectid;

    dispatch(checkedForCompareChanged({ responseid, checkedstate }));

    dispatch(
      apiCallBegan({
        serviceUrl: process.env.REACT_APP_BASICSERVICE,
        url: "/api/responses/updatecheckedforcompare",
        method: "post",
        data: { prospectid, checkedforcompare: checkedstate, responseid },
      })
    );

    return;
  };

export const changeSelectedWithOtherDispatches =
  ({ code }) =>
  (dispatch, getState) => {
    const prospectid = getState().entities.quotes.prospectid;

    const { includance, selected } =
      getState().entities.quotes.insurerdetails[code];

    if (includance === PossibleIncludanceValues.includedinpast) {
      dispatch(changeSelected({ code }));
    } else {
      // add or remove response as per the selected

      if (!selected) {
        // remove response
        let data = {
          prospectid: prospectid,
          oldstatus: "Added Insurer",
          newstatus: null,
          comment: code,
        };
        dispatch(addTransaction(data));
        dispatch(
          apiCallBegan({
            serviceUrl: process.env.REACT_APP_BASICSERVICE,
            url: "/api/responses/addsingleresponse",
            method: "post",
            data: {
              insurercode: code,
              prospectid: prospectid,
              rank: 200,
              source: "manual",
            },
            onStart: singleResponseAddInitialized.type,
            onSuccess: singleResponseAddSuccess.type,
            onError: singleResponseAddFailed.type,
            considerPayloads: true,
            onStartPayload: { code },
            onSuccessPayload: { code },
            onErrorPayload: { code },
          })
        );
      } else {
        // add response
        let data = {
          prospectid: prospectid,
          oldstatus: "Removed Insurer ",
          newstatus: null,
          comment: code,
        };
        dispatch(addTransaction(data));
        dispatch(
          apiCallBegan({
            serviceUrl: process.env.REACT_APP_BASICSERVICE,
            url: "/api/responses/removesingleresponse",
            method: "post",
            data: {
              insurercode: code,
              prospectid: prospectid,
            },
            onStart: singleResponseRemoveInitialized.type,
            onSuccess: singleResponseRemoveSuccess.type,
            onError: singleResponseRemoveFailed.type,
            considerPayloads: true,
            onStartPayload: { code },
            onSuccessPayload: { code },
            onErrorPayload: { code },
          })
        );
      }
    }

    return;
  };

export const updateQuoteyScoreInStoreAndDB = () => (dispatch, getState) => {
  let quotes = getState().entities.quotes;

  let oldQuoteyScores = getQuoteyScoresForAllResponses({
    responseorder: quotes.responseorder,
    responsedetails: quotes.responsedetails,
  });

  // dispatch updateQuoteyScoreInStore
  dispatch(udpateQuoteyScoreInStore());

  // see if there are any changes in quoteyscore
  quotes = getState().entities.quotes; // updating the quotes

  let newQuoteyScores = getQuoteyScoresForAllResponses({
    responseorder: quotes.responseorder,
    responsedetails: quotes.responsedetails,
  });

  // compare and collect which response id quotey score is changed

  let updatedQouteyScoresArray = compareOldVsNewQuoteyScores({
    oldQuoteyScores,
    newQuoteyScores,
    responseorder: quotes.responseorder,
  });

  // call api to update relevant quotey scores which got changed than earlier
  // data will be updatedQouteyScoresArray

  dispatch(
    apiCallBegan({
      serviceUrl: process.env.REACT_APP_BASICSERVICE,
      url: "/api/prospects/updatequoteyscore/",
      method: "post",
      data: {
        prospectid: quotes.prospectid,
        updatedQouteyScoresArray,
      },
    })
  );

  return;
};

//Exeption
export const updateCovers = createAction(
  changeCovers.type,
  function toggle(data) {
    return {
      payload: data,
    };
  }
);

//#endregion

// Selector
export const getTotalApiCallsToGetResponseInProgress = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.totalApiCallsToGetResponseInProgress
);

export const getApiQuoteCount = createSelector(
  (state) => state.entities.quotes,
  (quotes) => [quotes.totalApiCallsCompleted, quotes.totalApiCallsCalled]
);

export const getTotalApiCallsToGetResponseInProgressPreviousValue =
  createSelector(
    (state) => state.entities.quotes,
    (quotes) => quotes.totalApiCallsToGetResponseInProgressPreviousValue
  );

export const quoteyScoreLoadingDecider = (state) => {
  if (
    state.entities.quotes.totalApiCallsToGetResponseInProgress === 0 &&
    state.entities.quotes.totalApiCallsToGetResponseInProgressPreviousValue == 1
  )
    return false;
  if (
    state.entities.quotes.totalApiCallsToGetResponseInProgress === 0 &&
    state.entities.quotes.totalApiCallsToGetResponseInProgressPreviousValue ==
      null
  )
    return false;
  return true;
};

export const quoteyScoreRecalculateDecider = (state) => {
  if (
    state.entities.quotes.totalApiCallsToGetResponseInProgress === 0 &&
    state.entities.quotes.totalApiCallsToGetResponseInProgressPreviousValue == 1
  )
    return true;

  if (
    state.entities.quotes.totalApiCallsToGetResponseInProgress === 0 &&
    state.entities.quotes.totalApiCallsToGetResponseInProgressPreviousValue ==
      null
  )
    return true;

  return false;
};

export const getIsBpVerified = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.isbpVerified
);

export const getIRS = createSelector(
  (state) => state.entities.quotes,
  (quotes) => {
    return window.location.origin + "/iv?id=" + quotes.irs;
  }
);

export const getIRSCodeOnly = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.irs
);

export const getIRSAPP = createSelector(
  (state) => state.entities.quotes,
  (quotes) => {
    return window.location.origin + "/ivqf?id=" + quotes.irs;
  }
);

export const getCRS = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.crs
);

export const getVerifyingBp = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.verifyingbp
);

export const getProspectid = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.prospectid
);

export const getRecalculate = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.recalculate
);

export const getTotalSelected = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.totalselected
);

export const getResponseOrder = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.responseorder
);

export const getInsurerOrder = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.insurerorder
);
export const getInsurerOrderRemaining = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.insureorderremaining
);

export const getInsurerDetails = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.insurerdetails
);

export const getResponseDetails = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.responsedetails
);

export const getResponseDetailsForCompareAsArray = createSelector(
  (state) => state.entities.quotes,
  (quotes) =>
    Object.values(quotes.responsedetails).filter((it) => it.checkedforcompare)
);

export const getProspectDetails = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.prospectdetails.isbinded
);

export const getProespectInformation = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.prospectdetails
);

export const getCoversAndCoverTypesFromStore = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.coversAndCoverTypes
);

export const getSTCoptions = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.stcoptions
);
export const getInsurerCheckboxValue = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.insurerCheckBox
);

export const getSTCoptionsCoversDynTableOrder = createSelector(
  (state) => state.entities.quotes,
  (quotes) => {
    let returnValue = [];
    try {
      returnValue = quotes.stcoptions.covers.table.order;
      return returnValue;
    } catch {
      return [];
    }
  }
);
export const getSTCoptionsCoversDynTableDetails = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.stcoptions.covers.table.details
);
export const getSTCoptionsCoversDynTableDetailsReason = (rowid) => (state) =>
  state.entities.quotes.stcoptions.covers.table.details[rowid].reason;
export const getSTCoptionsCoversDynTableDetailsCover = (rowid) => (state) =>
  state.entities.quotes.stcoptions.covers.table.details[rowid].cover;

export const getArrayOfResponseSelectedForCompare = createSelector(
  (state) => state.entities.quotes,
  (state) => state.entities.broker.information?.languagepreference || "en",
  (quotes, languagecode) => {
    return detailsOfResponseSelectedForCompare({
      responsedetails: quotes.responsedetails,
      responseorder: quotes.responseorder,
      insurerdetails: quotes.insurerdetails,
      languagecode: languagecode,
    });
  }
);

export const getSTCoptionsMeetYourTeam = createSelector(
  (state) => state.entities.quotes,
  (quotes) => {
    if ("teamselected" in quotes.stcoptions.meetyourteam)
      return quotes.stcoptions.meetyourteam.teamselected;
    return [];
  }
);

export const getSTCoptionsInsurerSelected = createSelector(
  (state) => state.entities.quotes,
  (quotes) => {
    if ("insurerselected" in quotes.stcoptions.comparison)
      return quotes.stcoptions.comparison.insurerselected;
    return null;
  }
);

export const getSTCoptionsAboutUsSelected = createSelector(
  (state) => state.entities.quotes,
  (quotes) => {
    if ("aboutusselected" in quotes.stcoptions.aboutus)
      return quotes.stcoptions.aboutus.aboutusselected;
    return null;
  }
);

export const getSTCoptionsReasonForRecommendation = createSelector(
  (state) => state.entities.quotes,
  (quotes) => {
    if ("reasonforrecommendation" in quotes.stcoptions.comparison)
      return quotes.stcoptions.comparison.reasonforrecommendation;
    return null;
  }
);

export const getResponseDetailsIsChecked = (id) =>
  createSelector(
    (state) => state.entities.quotes,
    (quotes) => quotes.responsedetails[id].checkedforcompare
  );

export const getCoverTable = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.covertable
);

export const getCoverTableAsArray = createSelector(
  (state) => state.entities.quotes,
  (quotes) => Object.values(quotes.covertable)
);

export const getCoverPlans = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.plans
);

export const getQuoteComment = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.comment
);

export const getLocationCovers = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.locationcovers
);

export const getCoverTypes = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.covertypes
);

export const getSTCModalStatus = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.isSTCModalopen
);
export const getSTCoptionValue = (key) =>
  createSelector(
    (state) => state.entities.quotes,
    (quotes) => quotes?.stcoptions[key]?.selected
  );

export const getComparisonTable = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.stcoptions["comparison"].tableValue || {}
);

export const getResponseDetail = (responseid) =>
  createSelector(
    (state) => state.entities.quotes,
    (quotes) => quotes.responsedetails[responseid]
  );

export const getInsuerName = ({ responseid }) =>
  createSelector(
    (state) => state.entities.quotes,
    (quotes) =>
      quotes.insurerdetails[quotes.responsedetails[responseid]["insurercode"]][
        "name"
      ]
  );

export const getInsurerBindType = ({ responseid }) =>
  createSelector(
    (state) => state.entities.quotes,
    (quotes) =>
      quotes.insurerdetails[quotes.responsedetails[responseid]["insurercode"]][
        "bindtype"
      ]
  );
export const getResponseBindType = ({ responseid }) =>
  createSelector(
    (state) => state.entities.quotes,
    (quotes) => quotes.responsedetails[responseid]["bindtype"]
  );
export const getInsurerQuotesBy = ({ responseid }) =>
  createSelector(
    (state) => state.entities.quotes,
    (quotes) =>
      quotes.insurerdetails[quotes.responsedetails[responseid]["insurercode"]][
        "quotesby"
      ]
  );
export const getInsuerEmail = ({ responseid }) =>
  createSelector(
    (state) => state.entities.quotes,
    (quotes) =>
      quotes.insurerdetails[quotes.responsedetails[responseid]["insurercode"]][
        "emails"
      ][0].email
  );
export const getInsuerEmailv2 = (name) =>
  createSelector(
    (state) => state.entities.quotes,
    (quotes) => quotes.insurerdetails[name]
  );

export const getInsurerCode = ({ responseid }) =>
  createSelector(
    (state) => state.entities.quotes,
    (quotes) => quotes.responsedetails[responseid]["insurercode"]
  );

export const getInsurerSource = ({ responseid }) =>
  createSelector(
    (state) => state.entities.quotes,
    (quotes) => quotes.responsedetails[responseid]["source"]
  );

export const getInsurerNameFromCode = ({ code }) =>
  createSelector(
    (state) => state.entities.quotes,
    (quotes) => quotes.insurerdetails[code]["name"]
  );

export const getCalculatedVerticalId = createSelector(
  (state) => state.entities.quotes,
  (quotes) => getCalculateVerticalIdFromProspectDoc(quotes.prospectdetails)
);

export const getCoverImportances = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.coverimportances
);
export const getBrokerTeamMembers = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.brokerteam
);

export const getAboutUsFromStore = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.aboutus
);

export const getInsurerEmailsFromCode = ({ code }) =>
  createSelector(
    (state) => state.entities.quotes,
    (quotes) => quotes.insurerdetails[code]["emails"]
  );

export const getInsurerStarratingFromCode =
  ({ code }) =>
  () =>
    createSelector(
      (state) => state.entities.quotes,
      (quotes) => quotes.insurerdetails[code]["starrating"]
    );

export const getInsurerSelectedEmailFromCode =
  ({ code }) =>
  () =>
    createSelector(
      (state) => state.entities.quotes,
      (quotes) => quotes.insurerdetails[code]["selectedemail"]
    );

export const getScrollResponsesDiv = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.isScrollDivResponses
);

export const getCovers = (responseid) => (state) =>
  state.entities.quotes.responsedetails[responseid]?.covers;

//
export const getParticularCover = (responseid, coverid) => (state) => {
  if (state.entities.quotes.responsedetails[responseid]) {
    if ("covers" in state.entities.quotes.responsedetails[responseid]) {
      if (
        state.entities.quotes.responsedetails[responseid].covers &&
        coverid in state.entities.quotes.responsedetails[responseid].covers
      ) {
        return state.entities.quotes.responsedetails[responseid].covers[
          coverid
        ];
      }
    }
  }

  return "--";
};

export const getExcelApiStatus = (responseid) => (state) =>
  state.entities.quotes.responsedetails[responseid].excelApiStatus;

export const getisViewQuoteModalOpen =
  ({ responseid }) =>
  (state) =>
    state.entities.quotes.responsedetails[responseid].isViewQuoteModalOpen;

export const getShowingSendToInsurers = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.showingSendToInsurers
);

export const getStiEmailData = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.stiEmailData
);

export const getIsLoading = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.isLoading
);

export const getSelectedInsurerEmails = createSelector(
  (state) => state.entities.quotes,
  (quotes) => {
    let allSelectedEmails = [];
    for (let code in quotes.insurerdetails) {
      if (quotes.insurerdetails[code].selected) {
        allSelectedEmails.push(quotes.insurerdetails[code].selectedemail);
      }
    }

    return allSelectedEmails;
  }
);

export const getLocationAddresses = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.locationAddresses
);

export const getCheckedForCompareForResponseId =
  ({ responseid }) =>
  (state) => {
    if (
      state.entities.quotes.responsedetails[responseid] &&
      "checkedforcompare" in state.entities.quotes.responsedetails[responseid]
    )
      return state.entities.quotes.responsedetails[responseid]
        .checkedforcompare;

    return "NOT_EXISTED";
  };

export const getPolicyJustBinded = createSelector(
  (state) => state.entities.quotes,
  (quotes) => quotes.policyJustBinded
);
